import classNames from 'classnames';
import { esito, esitoTrKey } from 'components/scommesseTipo';
import { Tooltip, TooltipPosition } from 'components/tooltip';
import { selectIsVisualizzazioneEstesa } from 'features/settings/selectors';
import { useInfoTipoList, useIsLivePage, useIsMarcatoriScommessa } from 'features/sport/hooks';
import { sortInfoTipoHeaders } from 'features/sport/utils/utils';
import { useTranslation } from 'hooks/useLingUI';
import { VisualizationEnum } from 'lib/api/sport/sportScommesseBySlugResponse';
import { useTypedSelector } from 'lib/centralStore';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { ItemWithTranslation } from 'types/swagger';
import { calculateArraySingleHeaderWidth, calculateHeaderWidth, isTruthy } from 'utility/functions';
import { SportTableScommesseInfoAggSelector } from '../sportTableScommesseInfoAggSelector';
import styles from './SportTableScommesseHeadersSticky.module.scss';

export type SportTableScommesseHeadersStickyProps = ItemWithTranslation<{ title?: string }> & {
  infoTipoScommessaKeyList: Array<string | number>;
  isSubHeaderVisible?: boolean;
  isInfoAggVisible?: boolean;
  className?: string;
};

export const SportTableScommesseHeadersSticky = ({
  title,
  titleTrKey,
  className,
  isInfoAggVisible = true,
  isSubHeaderVisible = true,
  infoTipoScommessaKeyList,
}: SportTableScommesseHeadersStickyProps) => {
  const isMarcatore = useIsMarcatoriScommessa();
  const isEstesa = useTypedSelector(selectIsVisualizzazioneEstesa);
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const isLive = useIsLivePage();

  const fragment = useMemo(() => {
    const [fragment] = asPath.split('/').filter((x) => x?.length > 0);
    return fragment;
  }, [asPath]);

  const isCartOpen = useTypedSelector(({ carrello }) => carrello.isOpen);

  const { infoTipoScommessaList, headerSequence } = useInfoTipoList(infoTipoScommessaKeyList, true);
  const isAntepost = infoTipoScommessaList?.some(
    ({ tipoVisualizzazione }) => tipoVisualizzazione === VisualizationEnum.Antepost
  );

  return (
    <div
      className={classNames(styles.stickyContainer, className ?? '', {
        [styles.withTitle]: title,
        [styles.antepost]: isAntepost,
      })}
      suppressHydrationWarning
    >
      {title && (
        <p className={styles.title} suppressHydrationWarning>
          {t(titleTrKey, title)}
        </p>
      )}
      <div className={styles.colDx} data-fragment={fragment}>
        {infoTipoScommessaList?.map((infoTipoScommessa, index) => {
          if (!infoTipoScommessa) return null;

          const {
            key: infoTipoScommessaKey,
            headers,
            toolTip,
            headersTrKey,
            toolTipTrKey,
            numeroEsiti: numeroEsitiFromInfoTipoScommessa,
            descrizione,
            descrizioneTrKey,
            tipoVisualizzazione,
            sizeInfoAggiuntive,
            numeroInfoAggiuntive,
            headersInfoAggiuntive,
            headersInfoAggiuntiveTrKey,
          } = sortInfoTipoHeaders(infoTipoScommessa, headerSequence);
          const numeroEsiti = isMarcatore && infoTipoScommessaKeyList.length > 1 ? 1 : numeroEsitiFromInfoTipoScommessa;

          const isSelectorEnabled =
            !isLive &&
            ![VisualizationEnum.RecursiveInfoAgg, VisualizationEnum.MultipleInfoAggEsitiUnderThree].includes(
              tipoVisualizzazione
            ) &&
            !isMarcatore;

          const key = `${descrizione}_${index}`;

          if (tipoVisualizzazione === VisualizationEnum.Antepost) {
            return <React.Fragment key={key} />;
          }

          const headerBoxesWidth = calculateArraySingleHeaderWidth(
            tipoVisualizzazione,
            sizeInfoAggiuntive,
            numeroEsiti,
            headers.filter((elemento) => elemento !== 'ALTRO'), // TODO capire come fare ad escludere il controllo sui risultati non visibili nell'elenco
            isMarcatore
          );

          const headerWidth = calculateHeaderWidth(
            tipoVisualizzazione,
            sizeInfoAggiuntive,
            numeroEsiti,
            headers.filter((elemento) => elemento !== 'ALTRO'), // TODO capire come fare ad escludere il controllo sui risultati non visibili nell'elenco
            isMarcatore
          );

          const txtTooltip = t(toolTipTrKey, toolTip);
          const txtDescrition = t(descrizioneTrKey, descrizione) ?? '-';

          let keys: Array<{ text: string; isInfoAgg?: boolean }> = [];
          let txts: Array<{ text: string; isInfoAgg?: boolean }> = [];

          if (isTruthy(numeroInfoAggiuntive) && isInfoAggVisible) {
            txts = (headersInfoAggiuntive ?? [])
              .slice(0, numeroInfoAggiuntive)
              .map((header) => ({ text: header, isInfoAgg: true }));
            keys = (headersInfoAggiuntiveTrKey ?? []).slice(0, numeroInfoAggiuntive).map((header) => ({
              text: header,
              isInfoAgg: true,
            }));
          }

          if (
            [
              VisualizationEnum.EsitiOverThree,
              VisualizationEnum.RecursiveInfoAgg,
              VisualizationEnum.SingleInfoAggEsitiOverThree,
            ].includes(tipoVisualizzazione)
          ) {
            keys.push({ text: esitoTrKey });
            txts.push({ text: esito });
          } else {
            keys.push(...(headersTrKey ?? []).slice(0, numeroEsiti).map((header) => ({ text: header })));
            txts.push(...(headers ?? []).slice(0, numeroEsiti).map((header) => ({ text: header })));
          }

          return (
            <div
              key={key}
              style={{ width: headerWidth }}
              className={classNames(styles.headersWrapper, { [styles.cartOpen]: isCartOpen })}
            >
              <p className={classNames(styles.descriptionWrapper, { [styles.withTooltip]: toolTip })}>
                <span className={styles.description}>{txtDescrition}</span>&nbsp;
                {txtTooltip && (
                  <Tooltip
                    content={txtTooltip}
                    position={TooltipPosition.BOTTOM_LEFT}
                    iconColor="--color-on-bg-secondary"
                  />
                )}
              </p>
              {isSubHeaderVisible && (
                <ul className={styles.list}>
                  {txts.map((header, index) => {
                    const translatedHeader = t(keys[index]?.text, header?.text);

                    if (isSelectorEnabled && header.isInfoAgg && !isEstesa) {
                      return (
                        <SportTableScommesseInfoAggSelector
                          header={translatedHeader}
                          infoTipoScommessaKey={infoTipoScommessaKey ?? ''}
                          key={`index_${infoTipoScommessaKey}`}
                          width={headerBoxesWidth[index]}
                        />
                      );
                    }

                    return (
                      <li
                        key={`${header}_${index}`}
                        style={{ width: `${headerBoxesWidth[index]}px` }}
                        className={styles.header}
                      >
                        {translatedHeader}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
